import React, { useEffect, useState } from 'react';
import { Card, Col, Row, Form, Button, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShieldHalved } from '@fortawesome/free-solid-svg-icons';
import FormResultMessage from './FormResultMessage';
import {
  fetchHVACampaigns,
  HVACampaign,
  fetchRiskElementCampaigns,
  assignRiskElementCampaigns
} from 'services/hvaservicecampaign.service';
import {
  fetchHVARiskCategories,
  fetchHVARiskElements,
  HVARiskCategory,
  HVARiskElement
} from 'services/hvarisk.service';

interface RiskElementWithCategory extends HVARiskElement {
  category?: HVARiskCategory;
}

const RiskAssignmentForm2: React.FC = () => {
  const [campaigns, setCampaigns] = useState<HVACampaign[]>([]);
  const [riskCategories, setRiskCategories] = useState<HVARiskCategory[]>([]);
  const [riskElements, setRiskElements] = useState<RiskElementWithCategory[]>(
    []
  );
  const [selectedRiskElement, setSelectedRiskElement] = useState<number | null>(
    null
  );
  const [selectedCampaigns, setSelectedCampaigns] = useState<Set<number>>(
    new Set()
  );

  const [message, setMessage] = useState<string | null>(null);
  const [messageType, setMessageType] = useState<
    'successupdate' | 'errorupdate'
  >('successupdate');
  const [modalVisible, setModalVisible] = useState<boolean>(false);

  // Carga inicial de datos
  useEffect(() => {
    const loadData = async () => {
      try {
        // Cargar campañas
        const campaignsData = await fetchHVACampaigns();
        setCampaigns(campaignsData);

        // Cargar categorías de riesgo
        const categoriesData = await fetchHVARiskCategories();
        setRiskCategories(categoriesData);

        // Cargar elementos de riesgo
        const elementsData = await fetchHVARiskElements();

        // Enriquecer elementos con su categoría
        const enrichedElements = elementsData.map(element => {
          const category = categoriesData.find(
            cat => cat.id === element.categoryid
          );
          return { ...element, category };
        });

        setRiskElements(enrichedElements);
      } catch (error) {
        console.error('Error loading data:', error);
      }
    };

    loadData();
  }, []);

  // Cargar campañas que tienen asignado el elemento de riesgo seleccionado
  useEffect(() => {
    const loadRiskElementCampaigns = async () => {
      if (selectedRiskElement !== null) {
        try {
          const assignedCampaignIds =
            await fetchRiskElementCampaigns(selectedRiskElement);
          setSelectedCampaigns(new Set(assignedCampaignIds));
        } catch (error) {
          console.error('Error loading risk element campaigns:', error);
          setSelectedCampaigns(new Set());
        }
      } else {
        setSelectedCampaigns(new Set());
      }
    };

    loadRiskElementCampaigns();
  }, [selectedRiskElement]);

  const handleRiskElementSelect = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setSelectedRiskElement(Number(event.target.value));
  };

  const handleCampaignSelect = (campaignId: number) => {
    setSelectedCampaigns(prev => {
      const newSet = new Set(prev);
      if (newSet.has(campaignId)) {
        newSet.delete(campaignId);
      } else {
        newSet.add(campaignId);
      }
      return newSet;
    });
  };

  const handleAssign = async () => {
    if (selectedRiskElement !== null) {
      try {
        await assignRiskElementCampaigns(
          selectedRiskElement,
          Array.from(selectedCampaigns)
        );

        const selectedElement = riskElements.find(
          el => el.id === selectedRiskElement
        );

        if (selectedCampaigns.size > 0) {
          setMessage(
            `Successfully assigned "${selectedElement?.name}" to ${selectedCampaigns.size} campaign(s).`
          );
        } else {
          setMessage(
            `Risk element "${selectedElement?.name}" has been removed from all campaigns.`
          );
        }
        setMessageType('successupdate');
      } catch (error) {
        setMessage(
          'An error occurred while updating campaign assignments: ' + error
        );
        setMessageType('errorupdate');
      }
      setModalVisible(true);
    } else {
      alert('Please select a risk element.');
    }
  };

  const closeModal = () => {
    setModalVisible(false);
  };

  // Agrupar campañas por servicio para mejor organización
  const campaignsByService = campaigns.reduce(
    (acc, campaign) => {
      if (!acc[campaign.servicename]) {
        acc[campaign.servicename] = [];
      }
      acc[campaign.servicename].push(campaign);
      return acc;
    },
    {} as { [serviceName: string]: HVACampaign[] }
  );

  return (
    <Row className="p-0">
      <Col className="">
        <h3 className="px-2 text-secondary-darker">Risk Elements</h3>
        <Card className="bg-body">
          <Card.Body>
            <div
              className="scrollbar px-2"
              style={{ height: '350px', overflowY: 'scroll' }}
            >
              {riskCategories.map(category => {
                const categoryElements = riskElements.filter(
                  el => el.categoryid === category.id && el.active
                );

                return (
                  <div key={category.id} className="mb-3">
                    <div className="d-flex align-items-center p-2 bg-light rounded">
                      <FontAwesomeIcon
                        icon={faShieldHalved}
                        className={`me-2`}
                      />
                      <strong>{category.name}</strong>
                      <Badge className="ms-auto bg-primary">
                        {categoryElements.length} elements
                      </Badge>
                    </div>

                    <div className="ms-3 mt-2">
                      {categoryElements.map(element => (
                        <Form.Check
                          key={element.id}
                          type="radio"
                          name="riskElements"
                          id={`riskcat2-${element.id}`}
                          value={element.id}
                          onChange={handleRiskElementSelect}
                          checked={selectedRiskElement === element.id}
                          className="mb-2"
                        >
                          <Form.Check.Input
                            id={`risk2-${element.id}`}
                            type="radio"
                            name="riskElements"
                            value={element.id}
                            checked={selectedRiskElement === element.id}
                            onChange={handleRiskElementSelect}
                          />
                          <Form.Check.Label>
                            <span style={{ fontSize: '0.9rem' }}>
                              {element.name}
                            </span>
                          </Form.Check.Label>
                        </Form.Check>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </Card.Body>
        </Card>
      </Col>

      <Col className="campaigns-column">
        <h3 className="px-2 text-secondary-darker">Campaigns</h3>
        <Card className="bg-body">
          <Card.Body>
            <div
              className="scrollbar px-2"
              style={{ height: '350px', overflowY: 'scroll' }}
            >
              {Object.entries(campaignsByService).map(
                ([serviceName, serviceCampaigns]) => (
                  <div key={serviceName}>
                    {serviceCampaigns.map(campaign => (
                      <Form.Check
                        id={`risk2-${campaign.id}`}
                        key={campaign.id}
                        type="checkbox"
                        label={`(${serviceName}) - ${campaign.name}`}
                        onChange={() => handleCampaignSelect(campaign.id)}
                        checked={selectedCampaigns.has(campaign.id)}
                      />
                    ))}
                  </div>
                )
              )}
            </div>
          </Card.Body>
        </Card>

        <div className="primary pt-3">
          <Button
            className="btn btn-block px-4"
            onClick={handleAssign}
            disabled={!selectedRiskElement}
          >
            <FontAwesomeIcon icon={faShieldHalved} className="me-2 fs-8" />
            Assign risk element to selected campaigns
          </Button>
        </div>
      </Col>

      <FormResultMessage
        show={modalVisible}
        message={message}
        messageType={messageType}
        logout={false}
        onClose={closeModal}
      />
    </Row>
  );
};

export default RiskAssignmentForm2;
